import React from 'react';
import { Clock, AlertTriangle, FileText } from 'lucide-react';
import type { CSWState } from '@/lib/services/whatsapp/types';
import { ResponseWindowTimer } from '@/components/messages/ResponseWindowTimer';

interface Props {
  state: CSWState | null;
  /** Callback per aprire selector template (mostrato solo quando CSW chiusa). */
  onOpenTemplates?: () => void;
  /** Chiamato quando il conto alla rovescia arriva a zero: ricarica lo stato della finestra. */
  onExpired?: () => void;
}

/**
 * Banner sopra il composer che comunica lo stato della finestra 24h (CSW).
 *
 * Tre stati visivi:
 *  - Aperta > 1h         → verde "Finestra aperta · X ore rimanenti"
 *  - Aperta <= 1h        → giallo "Finestra in scadenza · Xm" (reminder ultima ora)
 *  - Chiusa / mai aperta → rosso "Solo template" + CTA "Invia template"
 *
 * Per WAHA (state=null) NON renderizza nulla: WAHA non ha vincolo CSW.
 * Per Cloud senza inbound mai ricevuto (state.cswExpiresAt=null) mostra il box rosso
 * perché la finestra non è mai stata aperta e serve un template per iniziare.
 */
export function CswBanner({ state, onOpenTemplates, onExpired }: Props) {
  if (!state) return null;

  const { isOpen, minutesRemaining, cswExpiresAt, fepExpiresAt } = state;

  if (isOpen && minutesRemaining !== null) {
    const reminder = minutesRemaining <= 60;
    const display = formatRemaining(minutesRemaining);

    const cls = reminder
      ? 'bg-amber-50 border-amber-200 text-amber-900 dark:bg-amber-900/20 dark:border-amber-700 dark:text-amber-100'
      : 'bg-emerald-50 border-emerald-200 text-emerald-900 dark:bg-emerald-900/20 dark:border-emerald-700 dark:text-emerald-100';
    const icon = reminder
      ? <AlertTriangle className="h-4 w-4 flex-shrink-0" />
      : <Clock className="h-4 w-4 flex-shrink-0" />;

    // Scadenza vera: CSW o Free Entry Point (72h), la piu' lontana tra le due aperte.
    const deadlines = [cswExpiresAt, fepExpiresAt].filter((d): d is Date => d instanceof Date && d.getTime() > Date.now());
    const until = deadlines.length ? new Date(Math.max(...deadlines.map((d) => d.getTime()))) : new Date(Date.now() + minutesRemaining * 60_000);
    return (
      <div className={`flex items-center gap-2 px-3 py-2 border-b text-xs ${cls}`}>
        {icon}
        <span className="flex-1 flex items-center gap-2 flex-wrap">
          <ResponseWindowTimer
            className="!text-inherit"
            phases={[
              {
                until,
                label: reminder ? 'Finestra in scadenza tra' : 'Finestra di risposta aperta per',
                help: 'Regola di WhatsApp Business: dopo un messaggio del cliente hai 24 ore per rispondere liberamente (72 ore se è arrivato da un annuncio Click-to-WhatsApp). Il conto alla rovescia riparte a ogni suo nuovo messaggio. Scaduta la finestra puoi inviare solo template approvati da Meta.',
              },
            ]}
            expired={{ label: 'Finestra di risposta chiusa', help: 'Puoi inviare solo template approvati da Meta finché il cliente non scrive di nuovo.' }}
            onPhaseChange={onExpired}
          />
          {reminder && <span className="opacity-80">Dopo potrai inviare solo template approvati ({display}).</span>}
        </span>
      </div>
    );
  }

  // Chiusa o mai aperta
  return (
    <div className="flex items-center gap-2 px-3 py-2 border-b text-xs bg-red-50 border-red-200 text-red-900 dark:bg-red-900/20 dark:border-red-700 dark:text-red-100">
      <AlertTriangle className="h-4 w-4 flex-shrink-0" />
      <span className="flex-1">
        {cswExpiresAt
          ? <>Finestra di risposta <strong>chiusa</strong>. Puoi inviare solo template approvati da Meta.</>
          : <>Nessuna conversazione attiva. Inizia inviando un <strong>template approvato</strong>.</>
        }
      </span>
      {onOpenTemplates && (
        <button
          type="button"
          onClick={onOpenTemplates}
          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium bg-red-600 hover:bg-red-700 text-white transition-colors"
        >
          <FileText className="h-3.5 w-3.5" />
          Invia template
        </button>
      )}
    </div>
  );
}

function formatRemaining(minutes: number): string {
  if (minutes <= 0) return '0m';
  if (minutes < 60) return `${minutes}m`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h < 24) return m > 0 ? `${h}h ${m}m` : `${h}h`;
  const d = Math.floor(h / 24);
  const remH = h % 24;
  return remH > 0 ? `${d}g ${remH}h` : `${d}g`;
}
